/**
 * Lifecycle & Activation Sequence Templates
 * AI Copilot-specific sequences for Copilotly
 *
 * Key insights:
 * - Activation within first 3 days is critical
 * - First AI generation = 60% higher retention
 * - First copilot session = 80% higher retention
 * - Monitoring usage patterns drives engagement
 */

import { SequenceTemplate } from "./types";

export const ACTIVATION_INCOMPLETE: SequenceTemplate = {
  id: "activation-incomplete",
  name: "Activation Incomplete (No Generation)",
  type: "lifecycle",
  description: "Re-engage users who signed up but haven't used an AI copilot yet",
  strategy: "Critical window: users who don't make their first AI generation in 3 days rarely come back. Show value, offer help, highlight use cases. This is your most important sequence.",
  icon: "WarningOutlined",
  color: "#ef4444",
  expectedOutcomes: [
    "40%+ activation recovery",
    "Reduced early churn",
    "Friction point identification",
    "Support opportunity"
  ],
  bestPractices: [
    "Send within 24 hours of signup",
    "Offer quick-start prompts",
    "Include video walkthrough",
    "Offer live chat/call help",
    "Ask what's blocking them"
  ],
  trigger: {
    eventType: "signup",
    delay: { value: 1, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "inactive",
      engagementLevel: "low"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active", "trialing"]
    }
  },
  emails: [
    {
      name: "Day 1 - Quick Start Help",
      subject: "Need help getting started with your AI copilot?",
      suggestedTemplateId: "onboarding-day1",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Offer assistance + example prompts",
      variables: [
        { key: "quick_start_url", defaultValue: "{{dashboard_url}}" },
        { key: "video_link", defaultValue: "{{tutorial_video}}" }
      ]
    },
    {
      name: "Day 2 - Copilot Showcase",
      subject: "See what your AI copilot can do for you",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 1, unit: "days" },
      purpose: "Showcase copilot capabilities to spark interest"
    },
    {
      name: "Day 3 - Personal Help",
      subject: "{{first_name}}, can I help you get started?",
      suggestedTemplateId: "invitation",
      delayAfterPrevious: { value: 1, unit: "days" },
      purpose: "Offer personal onboarding assistance"
    },
    {
      name: "Day 5 - Feedback Request",
      subject: "What's holding you back?",
      suggestedTemplateId: "product-survey",
      delayAfterPrevious: { value: 2, unit: "days" },
      purpose: "Understand blockers"
    }
  ],
  tags: ["activation", "onboarding", "recovery", "critical"],
  bestFor: ["AI platforms", "SaaS products", "Self-service tools"],
  difficulty: "beginner",
  setupTime: "15 minutes"
};

export const FIRST_GENERATION_SUCCESS: SequenceTemplate = {
  id: "first-generation-success",
  name: "First AI Generation Success",
  type: "lifecycle",
  description: "Celebrate and guide users after their first AI copilot generation",
  strategy: "First generation is THE moment. Strike while iron is hot. Guide to explore more copilots, install browser extension, and try different use cases. Users who explore have 3x retention.",
  icon: "TrophyOutlined",
  color: "#10b981",
  expectedOutcomes: [
    "80%+ extension installation rate",
    "Multiple copilot exploration within 48 hours",
    "3x retention improvement",
    "Feature discovery"
  ],
  bestPractices: [
    "Celebrate immediately",
    "Clear next step: try another copilot",
    "Show what else they can do",
    "Offer browser extension",
    "Highlight credits remaining"
  ],
  trigger: {
    eventType: "firstGeneration",
    delay: { value: 0, unit: "minutes" }
  },
  segmentation: {
    behavior: {
      usagePattern: "any",
      engagementLevel: "any"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active", "trialing"]
    }
  },
  emails: [
    {
      name: "Congratulations!",
      subject: "You just made your first AI generation!",
      suggestedTemplateId: "product-launch",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Celebrate + guide to explore more",
      variables: [
        { key: "copilot_name", defaultValue: "{{used_copilot}}" },
        { key: "explore_link", defaultValue: "{{copilots_url}}" }
      ]
    },
    {
      name: "Browser Extension Guide",
      subject: "Get AI help on any website with our extension",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 1, unit: "days" },
      purpose: "Promote browser extension installation"
    },
    {
      name: "Explore More Copilots",
      subject: "Have you tried the {{suggested_copilot}} copilot?",
      suggestedTemplateId: "product-reminder",
      delayAfterPrevious: { value: 2, unit: "days" },
      purpose: "Encourage exploring different copilots"
    },
    {
      name: "Pro Tips",
      subject: "Get more from your AI copilots",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Share power-user tips and tricks"
    }
  ],
  tags: ["milestone", "activation", "first-generation", "success"],
  bestFor: ["AI platforms", "Copilot tools", "Productivity apps"],
  difficulty: "beginner",
  setupTime: "15 minutes"
};

export const CREDITS_LOW_ALERT: SequenceTemplate = {
  id: "credits-low-alert",
  name: "Credits Running Low",
  type: "lifecycle",
  description: "Alert users when their AI credits are running low",
  strategy: "Low credits = risk of churn or frustration. Proactive notification helps users plan ahead, upgrade, or top up. Reduces support tickets and prevents surprise interruptions.",
  icon: "WarningOutlined",
  color: "#f59e0b",
  expectedOutcomes: [
    "30%+ upgrade conversion",
    "Reduced churn from credit exhaustion",
    "Better user experience",
    "Proactive engagement"
  ],
  bestPractices: [
    "Send when credits drop below 20%",
    "Show usage trends",
    "Offer upgrade options",
    "Explain credit renewal",
    "Provide top-up option"
  ],
  trigger: {
    eventType: "creditsLow",
    delay: { value: 0, unit: "minutes" }
  },
  segmentation: {
    behavior: {
      usagePattern: "active",
      engagementLevel: "any"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active", "trialing"]
    }
  },
  emails: [
    {
      name: "Credits Running Low",
      subject: "Heads up: Only {{credits_remaining}} credits left",
      suggestedTemplateId: "credits-low",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Alert user about low credits + upgrade options",
      variables: [
        { key: "credits_remaining", defaultValue: "{{user_credits}}" },
        { key: "upgrade_url", defaultValue: "{{pricing_url}}" }
      ]
    },
    {
      name: "Upgrade Benefits",
      subject: "Unlock unlimited AI with {{product_name}} Pro",
      suggestedTemplateId: "product-upsell",
      delayAfterPrevious: { value: 2, unit: "days" },
      purpose: "Highlight Pro plan benefits"
    }
  ],
  tags: ["credits", "upsell", "retention", "alert"],
  bestFor: ["Credit-based platforms", "AI SaaS", "Usage-based pricing"],
  difficulty: "beginner",
  setupTime: "10 minutes"
};

export const USAGE_INSIGHTS: SequenceTemplate = {
  id: "usage-insights",
  name: "Weekly AI Usage Insights",
  type: "lifecycle",
  description: "Send weekly reports on AI copilot usage and productivity gains",
  strategy: "Data drives engagement. Users who see their productivity gains have 2x retention. Weekly insights keep users connected and demonstrate value.",
  icon: "LineChartOutlined",
  color: "#8b5cf6",
  expectedOutcomes: [
    "Increased dashboard visits",
    "Higher feature adoption",
    "Better perceived value",
    "Reduced churn"
  ],
  bestPractices: [
    "Send same day/time weekly",
    "Highlight key metrics",
    "Show hours/time saved",
    "Compare to previous period",
    "Suggest new copilots to try"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 7, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "active",
      engagementLevel: "any"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Weekly Usage Report",
      subject: "Your AI report: {{generations_count}} generations this week",
      suggestedTemplateId: "weekly-digest",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Weekly usage summary with insights",
      variables: [
        { key: "generations_count", defaultValue: "{{weekly_generations}}" },
        { key: "hours_saved", defaultValue: "{{time_saved}}" },
        { key: "top_copilot", defaultValue: "{{most_used_copilot}}" },
        { key: "credits_remaining", defaultValue: "{{credits_left}}" }
      ]
    }
  ],
  tags: ["analytics", "usage", "weekly", "engagement"],
  bestFor: ["AI platforms", "Productivity tools", "SaaS products"],
  difficulty: "beginner",
  setupTime: "10 minutes"
};

export const EXPLORE_MORE_COPILOTS: SequenceTemplate = {
  id: "explore-copilots",
  name: "Explore More Copilots",
  type: "lifecycle",
  description: "Encourage users to try additional AI copilots beyond their first",
  strategy: "Users who try 3+ copilots have 90% retention. After first copilot success, suggest related copilots. Multi-copilot users are your best customers.",
  icon: "AppstoreAddOutlined",
  color: "#f59e0b",
  expectedOutcomes: [
    "40%+ try second copilot",
    "Increased session duration",
    "90%+ retention",
    "Higher credit usage"
  ],
  bestPractices: [
    "Trigger after first generation success",
    "Suggest complementary copilots",
    "Highlight popular copilots",
    "Show use case examples",
    "Personalize based on profession"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 14, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "active",
      engagementLevel: "high"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Copilot Recommendation",
      subject: "{{first_name}}, try the {{suggested_copilot}} copilot",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Suggest a related copilot based on usage",
      variables: [
        { key: "current_copilot", defaultValue: "{{primary_copilot}}" },
        { key: "suggested_copilot", defaultValue: "Marketing Copilot" }
      ]
    },
    {
      name: "Popular Copilots",
      subject: "5 copilots our top users love",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Showcase popular copilots"
    },
    {
      name: "Success Stories",
      subject: "How {{company_name}} uses 3 copilots to save 10+ hours/week",
      suggestedTemplateId: "product-feedback",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Multi-copilot success stories"
    }
  ],
  tags: ["engagement", "expansion", "exploration", "growth"],
  bestFor: ["AI platforms", "Multi-product tools", "Platform businesses"],
  difficulty: "intermediate",
  setupTime: "15 minutes"
};

export const AT_RISK_USER_INTERVENTION: SequenceTemplate = {
  id: "at-risk-intervention",
  name: "At-Risk User Intervention",
  type: "lifecycle",
  description: "Proactively reach out to users showing churn signals",
  strategy: "Identify at-risk users before they cancel: declining usage, no logins, unused credits. Proactive outreach can save 30% of at-risk users.",
  icon: "AlertOutlined",
  color: "#ef4444",
  expectedOutcomes: [
    "30%+ at-risk user recovery",
    "Churn reduction",
    "Valuable feedback collection",
    "Support opportunity"
  ],
  bestPractices: [
    "Identify signals: usage drop, no login",
    "Reach out personally",
    "Offer help, not sales pitch",
    "Ask about their experience",
    "Offer incentive to stay"
  ],
  trigger: {
    eventType: "inactivity",
    delay: { value: 14, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "inactive",
      engagementLevel: "low"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Check-in Email",
      subject: "{{first_name}}, is everything okay?",
      suggestedTemplateId: "re-engagement",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Personal check-in + offer help",
      variables: [
        { key: "last_login", defaultValue: "{{days_since_login}} days ago" },
        { key: "support_link", defaultValue: "{{calendar_link}}" }
      ]
    },
    {
      name: "Feedback Request",
      subject: "Quick question about your experience",
      suggestedTemplateId: "product-survey",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Understand what's wrong"
    },
    {
      name: "Stay Incentive",
      subject: "We want to make it right - special offer inside",
      suggestedTemplateId: "promotion",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Incentive to continue using"
    }
  ],
  tags: ["retention", "at-risk", "churn-prevention", "intervention"],
  bestFor: ["All SaaS products", "Subscription businesses"],
  difficulty: "intermediate",
  setupTime: "15 minutes"
};

export const SEASONAL_REACTIVATION: SequenceTemplate = {
  id: "seasonal-reactivation",
  name: "Seasonal Re-activation Campaign",
  type: "lifecycle",
  description: "Re-engage dormant users with seasonal campaigns",
  strategy: "Seasonal moments (New Year, Black Friday, Q4 planning) are natural re-engagement triggers. Combine urgency with relevance for 25%+ reactivation.",
  icon: "GiftOutlined",
  color: "#ec4899",
  expectedOutcomes: [
    "25%+ dormant user reactivation",
    "Revenue boost",
    "Updated user database",
    "Seasonal momentum"
  ],
  bestPractices: [
    "Plan campaigns around key dates",
    "Offer meaningful incentives",
    "Highlight what's new since they left",
    "Create urgency with deadlines",
    "Segment by inactivity duration"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 0, unit: "minutes" }
  },
  segmentation: {
    behavior: {
      usagePattern: "inactive",
      engagementLevel: "low"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active", "canceled"]
    }
  },
  emails: [
    {
      name: "Seasonal Offer",
      subject: "{{season}} Special: Come back to {{product_name}}",
      suggestedTemplateId: "promotion",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Seasonal reactivation offer",
      variables: [
        { key: "season", defaultValue: "New Year" },
        { key: "discount", defaultValue: "50%" },
        { key: "deadline", defaultValue: "{{offer_end_date}}" }
      ]
    },
    {
      name: "What's New",
      subject: "A lot has changed since you left...",
      suggestedTemplateId: "product-announcement",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Showcase new AI models and copilots"
    },
    {
      name: "Last Chance",
      subject: "Offer expires tomorrow - don't miss out",
      suggestedTemplateId: "promotion",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Urgency reminder"
    }
  ],
  tags: ["seasonal", "reactivation", "retention", "promotion"],
  bestFor: ["All businesses", "E-commerce", "Subscription SaaS"],
  difficulty: "beginner",
  setupTime: "15 minutes"
};

export const LIFECYCLE_TEMPLATES = [
  ACTIVATION_INCOMPLETE,
  FIRST_GENERATION_SUCCESS,
  CREDITS_LOW_ALERT,
  USAGE_INSIGHTS,
  EXPLORE_MORE_COPILOTS,
  AT_RISK_USER_INTERVENTION,
  SEASONAL_REACTIVATION
];
